import { device } from 'components/styles/MediaQuery';
import { countriesWithPhoneNumber } from 'helpers/countriesWithPhoneNumber';
import { maskPhone } from 'helpers/masks';
import {
  composeValidators,
  numberPhoneValidator,
  required,
} from 'helpers/validators';
import { useFieldState } from 'informed';
import { parsePhoneNumber } from 'libphonenumber-js';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { Input } from './Input';
import { Select } from './Select';

export const validatePhone = composeValidators(required, numberPhoneValidator);

interface InputPhoneNumberProps {
  field: string;
  initialValue?: string;
  initialCodeValue?: string;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  withValidate?: boolean;
  disabled?: boolean;
  error?: string;
}

export const InputPhoneNumber: FC<InputPhoneNumberProps> = ({
  field,
  withValidate,
  initialValue,
  initialCodeValue,
  disabled,
  ...props
}) => {
  const { t } = useTranslation();
  const fieldCodeState = useFieldState<string | undefined>(`${field}.code`);

  const currentCountry = useMemo(() => {
    if (fieldCodeState == null || fieldCodeState.value == null) return;

    return fieldCodeState.value;
  }, [fieldCodeState]);

  const countriesOptions = countriesWithPhoneNumber
    .sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    })
    .map(({ name, dialCode, iso2, flagUrl }) => ({
      name: `+${dialCode} | ${name}`,
      nameS: `+${dialCode} | ${name}`,
      nameV: `+${dialCode}`,
      value: iso2,
      icon: flagUrl,
    }));

  const {
    country: initialValueCountry,
    nationalNumber: initialValueNationalNumber,
  } = useMemo(() => {
    if (initialValue == null && initialCodeValue != null) {
      return {
        country: initialCodeValue,
        nationalNumber: undefined,
      };
    }

    if (initialValue == null)
      return {
        country: undefined,
        nationalNumber: undefined,
      };

    return parsePhoneNumber(initialValue);
  }, [initialValue, initialCodeValue]);

  return (
    <ScInputPhoneNumber>
      <Select
        animatedPlaceholder="Code"
        disabled={disabled}
        field={`${field}.code`}
        initialValue={initialValueCountry}
        minOptionHeight={48}
        options={countriesOptions}
        search
        selectedOptionWithoutIcon
        validate={required}
        withBorderMode
      />
      <Input
        {...props}
        animatedPlaceholder={t('glossary:number')}
        disabled={disabled}
        field={`${field}.number`}
        initialValue={initialValueNationalNumber as string}
        key={currentCountry}
        mask={(v) => maskPhone(v?.toString(), currentCountry)}
        validate={(v) => validatePhone(v, currentCountry)}
      />
    </ScInputPhoneNumber>
  );
};

const ScInputPhoneNumber = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 8px;

  @media (${device.mobileS}) {
    grid-template-columns: 120px auto;
  }
`;

export const ScInputNameSurname = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  @media (${device.mobileS}) {
    grid-template-columns: 1fr;
  }
`;

export const ScAddressInput = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  @media (${device.mobileS}) {
    grid-template-columns: 1fr;
  }
`;
